"use client";

import {
  Badge,
  Button,
  Item,
  ItemContent,
  Label,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@atlas/core";
import type { ReactNode } from "react";

interface IDetailsPanelItem {
  label: string;
  value: ReactNode;
}

interface IDetailsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  items: IDetailsPanelItem[];
  onEdit?: () => void;
  onDelete?: () => void;
  cancelLabel?: string;
  editLabel?: string;
  deleteLabel?: string;
  statusLabel?: string;
  header?: ReactNode;
  footer?: ReactNode;
}

export function DetailsPanel({
  open,
  onOpenChange,
  title,
  items,
  onEdit,
  onDelete,
  cancelLabel = "Cancel",
  editLabel = "Edit",
  deleteLabel = "Delete",
  statusLabel,
  header,
  footer,
}: IDetailsPanelProps) {
  const handleClose = () => onOpenChange(false);

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
      handleClose();
    }
  };

  const renderValue = (label: string, value: ReactNode) => {
    const strValue =
      value == null ? "—" : typeof value === "string" ? value : String(value);
    const isEmpty = value == null || strValue === "—";

    if (statusLabel && label === statusLabel && !isEmpty) {
      const isSuccess =
        strValue.toLowerCase().includes("updated") ||
        strValue.toLowerCase() === "active";
      return (
        <Badge
          variant={isSuccess ? "success" : "neutral"}
          appearance="ghost"
          size="sm"
          dot>
          {strValue}
        </Badge>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Label className="truncate max-w-full block">{strValue || "—"}</Label>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <Label>{strValue || "—"}</Label>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-lg font-bold text-primary">
            {title}
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="min-h-0 flex-1" viewportClassName="py-4">
          {header && <div className="pb-4">{header}</div>}
          {items.map(({ label, value }, index) => (
            <Item
              key={`${label}-${index}`}
              variant="default"
              size="sm"
              className="">
              <ItemContent className="flex-none w-1/3">{label}</ItemContent>
              <ItemContent className="truncate max-w-50">
                {typeof value === "string" ||
                typeof value === "number" ||
                value == null
                  ? renderValue(label, value)
                  : value}
              </ItemContent>
            </Item>
          ))}
          {footer}
        </ScrollArea>
        <SheetFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            {cancelLabel}
          </Button>
          {onEdit && <Button onClick={handleEdit}>{editLabel}</Button>}
          {onDelete && (
            <Button variant="destructive" onClick={onDelete}>
              {deleteLabel}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
